import React from 'react';
import { styled, Typography } from '@mui/material';
import { pink } from '@mui/material/colors';
import { format, isWeekend, parseISO } from 'date-fns';
import { sv } from 'date-fns/locale';
import AnimateHeight from 'react-animate-height';
import { IntervalEntryConfig, IntervalReportDay } from 'api/report/types';
import { Button } from 'components/Button/Button';
import Tooltip from 'components/interface/tooltip/Tooltip';
import {
    ResizeObserverCallback,
    useResizeObserverWithRef,
} from 'components/hooks/useResizeObserver';
import {
    getIntervalDraftMinutes,
    getIntervalDraftSummary,
} from 'report/logic/interval/calculations';
import {
    IntervalDayDraft,
    IntervalEntryDraft,
    IntervalEntryValidations,
} from 'report/logic/interval/editing';
import { DayCardEntry } from './DayCardEntry';
import { formatMinutes } from './formatting';

const DaysList = styled('div')(({ theme }) => ({
    userSelect: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
}));

const DaySection = styled('section')(({ theme }) => ({
    borderRadius: 16,
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
}));

const DayHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 1.5, 1),
}));

const DayLabel = styled('div', {
    shouldForwardProp: prop => prop !== '$holiday',
})<{ $holiday: boolean }>(({ theme, $holiday }) => ({
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    color: $holiday ? pink[900] : theme.palette.text.primary,
    whiteSpace: 'nowrap',
}));

const DayName = styled(Typography)({
    '&::first-letter': { textTransform: 'uppercase' },
});

const DayFooter = styled('div')(({ theme }) => ({
    padding: theme.spacing(1),
}));

const DayTotal = styled(Typography)({
    minWidth: '6em',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
});

interface Props {
    days: IntervalDayDraft[];
    entryValidation: IntervalEntryValidations;
    entryKinds: IntervalEntryConfig[];
    timeZone: string;
    readOnly: boolean;
    disabled: boolean;
    addDisabled: boolean;
    selectedEntryID: string | null;
    onAdd: (date: string) => void;
    onSelect: (entry: IntervalEntryDraft) => void;
    isMobile?: boolean;
}

interface DayCardProps
    extends Omit<Props, 'days'>, Pick<IntervalReportDay, 'date' | 'holiday'> {
    entries: IntervalEntryDraft[];
}

function DayCard({
    date,
    holiday,
    entries,
    entryValidation,
    entryKinds,
    timeZone,
    readOnly,
    disabled,
    addDisabled,
    selectedEntryID,
    onAdd,
    onSelect,
    isMobile = false,
}: DayCardProps) {
    const contentRef = React.useRef<HTMLDivElement | null>(null);
    const [height, setHeight] = React.useState<number | 'auto'>('auto');
    const handleResize = React.useCallback<ResizeObserverCallback>(entry => {
        setHeight(entry.contentRect.height);
    }, []);
    useResizeObserverWithRef(contentRef, handleResize);

    const parsedDate = parseISO(date);
    const { totalMinutes, sickMinutes } = getIntervalDraftSummary(
        entries,
        entryValidation
    );
    const hasTime = entries.some(
        entry =>
            getIntervalDraftMinutes(
                entry,
                entryValidation[entry.uuid]?.errors
            ) !== null
    );
    const isPublicHoliday = holiday?.IsPublicHoliday ?? false;
    const holidayLabel = holiday?.IsMajorHoliday
        ? 'Storhelg'
        : isPublicHoliday && !isWeekend(parsedDate)
          ? 'Helgdag'
          : null;
    return (
        <DaySection>
            <DayHeader>
                <Tooltip title={holiday?.Name?.trim()}>
                    <DayLabel $holiday={isPublicHoliday}>
                        <DayName
                            variant='body2'
                            color='inherit'
                            fontWeight={600}>
                            {format(parsedDate, 'EEEE', { locale: sv })}
                        </DayName>
                        <Typography
                            variant='caption'
                            color={
                                isPublicHoliday ? 'inherit' : 'text.secondary'
                            }>
                            {format(parsedDate, 'd MMM', { locale: sv })}
                        </Typography>
                        {holidayLabel && (
                            <Typography variant='caption' color='inherit'>
                                {holidayLabel}
                            </Typography>
                        )}
                    </DayLabel>
                </Tooltip>
                {entries.length > 0 && (
                    <DayTotal variant='body2' fontWeight={600}>
                        {hasTime
                            ? formatMinutes(totalMinutes + sickMinutes)
                            : '—'}
                    </DayTotal>
                )}
            </DayHeader>
            <AnimateHeight
                height={height}
                contentRef={contentRef}
                duration={200}
                disableDisplayNone>
                {entries.map(entry => (
                    <DayCardEntry
                        key={entry.uuid}
                        draft={entry}
                        validation={entryValidation[entry.uuid]}
                        entryKinds={entryKinds}
                        timeZone={timeZone}
                        selected={entry.uuid === selectedEntryID}
                        onClick={() => onSelect(entry)}
                        isMobile={isMobile}
                    />
                ))}
            </AnimateHeight>
            {!readOnly && (
                <DayFooter>
                    <Button
                        variant='secondary'
                        size='md'
                        icon='add'
                        fullWidth
                        disabled={disabled || addDisabled || !entryKinds.length}
                        onClick={() => onAdd(date)}>
                        Lägg till rad
                    </Button>
                </DayFooter>
            )}
        </DaySection>
    );
}

export function DayCards({ days, isMobile = false, ...props }: Props) {
    return (
        <DaysList>
            {days.map(({ date, holiday, entries }) => (
                <DayCard
                    key={date}
                    date={date}
                    holiday={holiday}
                    entries={entries}
                    isMobile={isMobile}
                    {...props}
                />
            ))}
        </DaysList>
    );
}
